import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"

export default function ShowForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(!!id)
  const [formData, setFormData] = useState({
    title: "",
    theme: "",
    date: "",
    ticket_url: "",
    active: false,
    status: "Waiting to begin"
  })

  const STATUSES = ["Waiting to begin", "First half", "Intermission", "Second half", "Complete"]

  useEffect(() => {
    if (id) {
      fetchShow()
    }
  }, [id])

  const fetchShow = async () => {
    try {
      const response = await fetch(`/api/admin/shows/${id}`)
      const data = await response.json()
      setFormData({
        title: data.title || "",
        theme: data.theme || "",
        date: data.date || "",
        ticket_url: data.ticket_url || "",
        active: data.active || false,
        status: data.status || "Waiting to begin"
      })
      setLoading(false)
    } catch (error) {
      console.error("Error fetching show:", error)
      alert("Error loading show")
      navigate("/admin/super_secret")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const url = id ? `/api/admin/shows/${id}` : "/api/admin/shows"
    const method = id ? "PUT" : "POST"

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ show: formData })
      })

      if (response.ok) {
        navigate("/admin/super_secret")
      } else {
        const data = await response.json()
        alert(`Error: ${data.errors?.join(", ")}`)
      }
    } catch (error) {
      console.error("Error saving show:", error)
      alert("Error saving show")
    }
  }

  const handleClearSuggestions = async () => {
    if (!window.confirm(
      "⚠️ WARNING: This will permanently delete ALL audience suggestions for this show and reset formatted askfors for all games. This action cannot be undone.\n\nAre you absolutely sure you want to continue?"
    )) {
      return
    }

    try {
      const response = await fetch(`/api/admin/shows/${id}/clear_suggestions`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        }
      })

      if (response.ok) {
        const data = await response.json()
        alert(data.message)
      } else {
        alert("Error clearing suggestions")
      }
    } catch (error) {
      console.error("Error clearing suggestions:", error)
      alert("Error clearing suggestions")
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/admin/super_secret")}
          className="mb-6 text-cyan-600 hover:text-cyan-700 font-semibold flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Admin
        </button>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">
            {id ? "Edit Show" : "Create New Show"}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-lg"
                placeholder="Enter show title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme
              </label>
              <input
                type="text"
                value={formData.theme}
                onChange={(e) =>
                  setFormData({ ...formData, theme: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="Show theme (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ticket URL
              </label>
              <input
                type="url"
                value={formData.ticket_url}
                onChange={(e) =>
                  setFormData({ ...formData, ticket_url: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                required
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-lg"
              >
                {STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
                className="h-5 w-5 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label htmlFor="active" className="ml-3 block text-sm font-medium text-gray-700">
                Set as Active (Current Show)
              </label>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors"
              >
                {id ? "Update Show" : "Create Show"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/super_secret")}
                className="px-8 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-semibold text-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Danger Zone - Only show when editing an existing show */}
          {id && (
            <div className="mt-8 p-6 border-2 border-red-300 rounded-lg bg-red-50">
              <h2 className="text-xl font-bold text-red-800 mb-2">
                Danger Zone
              </h2>
              <p className="text-sm text-red-700 mb-4">
                Warning: This action is permanent and cannot be undone.
              </p>
              <button
                type="button"
                onClick={handleClearSuggestions}
                className="px-6 py-3 rounded-lg font-semibold transition-colors"
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: '2px solid #991b1b'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
              >
                Clear All Suggestions & Reset Formatted Askfors
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
